import { Copy, RotateCcw, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { StatusIcon } from "@/features/attorney/views/Entries/StatusIcon";
import type { EntryStatus } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import { getDuplicateEntryTooltip } from "@/shared/utils/firm";
import {
	EntryForm,
	type EntryFormHandle,
} from "@/v2/components/entry-form/EntryForm";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import { Banner } from "@/v2/components/ui/banner";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "@/v2/components/ui/select";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { createEntryFormDefaults } from "@/v2/lib/entryPayloads";

export interface EditEntryDialogProps {
	entry: Entry | null;
	failedExports: ReadonlyMap<number, string>;
	status?: EntryStatus;
	onOpenChange: (open: boolean) => void;
	onResolveFeedback: (entryId: number) => void;
	onSubmit: (
		entry: Entry,
		values: TimeEntryFormValues,
		status?: EntryStatus,
	) => void;
	onExport: (entry: Entry, values: TimeEntryFormValues) => void;
	exportDisabledReason?: string;
	onDuplicate?: (entry: Entry) => void;
	onDelete?: (entry: Entry) => void;
	onRestore?: (entry: Entry) => void;
	title?: string;
	unitsReadOnly?: boolean;
	activeTimerReadOnlyNote?: ReactNode;
	narrativeSlot?: ReactNode;
	narrativeActionSlot?: ReactNode;
	unitsActionSlot?: ReactNode;
	hideGenerateNarrativeButton?: boolean;
	onRegenerateNarrative?: () => Promise<string>;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: EntryStatus; label: string }> = [
	{ value: "tracked", label: "Tracked" },
	{ value: "reviewed", label: "Reviewed" },
];

const STATUS_LABEL: Record<EntryStatus, string> = {
	tracked: "Tracked",
	reviewed: "Reviewed",
	exported: "Exported",
	deleted: "Deleted",
};

function StatusOption({ status }: { status: EntryStatus }) {
	return (
		<div className="flex items-center gap-2">
			<StatusIcon status={status} className="size-3.5" />
			<span>{STATUS_LABEL[status]}</span>
		</div>
	);
}

interface StatusSelectProps {
	status: EntryStatus | undefined;
	currentStatus: EntryStatus | undefined;
	onStatusChange: (status: EntryStatus) => void;
}

function StatusSelect({
	status,
	currentStatus,
	onStatusChange,
}: StatusSelectProps) {
	if (!status) return null;

	const isTerminalStatus = status === "exported";

	return (
		<Select
			value={currentStatus}
			onValueChange={(value) => onStatusChange(value as EntryStatus)}
			disabled={isTerminalStatus}
		>
			<SelectTrigger className="h-9 w-[126px] bg-card">
				{currentStatus ? (
					<StatusOption status={currentStatus} />
				) : (
					<span className="text-muted-foreground">Status</span>
				)}
			</SelectTrigger>
			<SelectContent>
				{!STATUS_OPTIONS.some((o) => o.value === status) && (
					<SelectItem value={status} disabled>
						<StatusOption status={status} />
					</SelectItem>
				)}
				{STATUS_OPTIONS.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						<StatusOption status={option.value} />
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

export function EditEntryDialog({
	entry,
	failedExports,
	status,
	onOpenChange,
	onResolveFeedback,
	onSubmit,
	onExport,
	exportDisabledReason,
	onDuplicate,
	onDelete,
	onRestore,
	title = "Edit Entry",
	unitsReadOnly = false,
	activeTimerReadOnlyNote,
	narrativeSlot,
	narrativeActionSlot,
	unitsActionSlot,
	hideGenerateNarrativeButton = false,
	onRegenerateNarrative,
}: EditEntryDialogProps) {
	const formRef = useRef<EntryFormHandle>(null);
	const [selectedStatus, setSelectedStatus] = useState<EntryStatus | undefined>(
		status,
	);

	useEffect(() => {
		setSelectedStatus(status);
	}, [status]);

	const isDeleted = status === "deleted";

	return (
		<ResponsiveDialog open={entry !== null} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				className="!max-h-[90vh]"
				title={title}
				headerClassName="p-4 pb-2"
				footer={
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							{entry && !isDeleted && onDelete ? (
								<TooltipButton
									type="button"
									variant="outline"
									size="icon"
									className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
									aria-label="Delete entry"
									tooltip="Delete entry"
									onClick={() => onDelete(entry)}
								>
									<Trash2 />
								</TooltipButton>
							) : null}
							{entry && !isDeleted && onDuplicate && (
								<TooltipButton
									type="button"
									variant="outline"
									size="icon"
									aria-label="Duplicate entry"
									tooltip={getDuplicateEntryTooltip()}
									onClick={() => onDuplicate(entry)}
								>
									<Copy />
								</TooltipButton>
							)}
						</div>
						<div className="flex items-center gap-2">
							{isDeleted ? (
								entry && onRestore ? (
									<TooltipButton
										type="button"
										size="sm"
										aria-label="Restore entry"
										tooltip="Restore entry"
										onClick={() => onRestore(entry)}
									>
										<RotateCcw />
										Restore
									</TooltipButton>
								) : null
							) : (
								<>
									<StatusSelect
										status={status}
										currentStatus={selectedStatus}
										onStatusChange={setSelectedStatus}
									/>
									<TooltipButton
										variant="outline"
										size="sm"
										disabled={!!exportDisabledReason}
										tooltip={exportDisabledReason ?? "Export"}
										onClick={() => formRef.current?.submitExport()}
									>
										Export
									</TooltipButton>
									<TooltipButton
										type="submit"
										form="edit-entry-form"
										size="sm"
										tooltip="Save"
									>
										Save
									</TooltipButton>
								</>
							)}
						</div>
					</div>
				}
			>
				{entry && (
					<EntryForm
						ref={formRef}
						formId="edit-entry-form"
						editedGroupId={entry.entryGroupId}
						timekeeperId={entry.timekeeperId}
						key={entry.id}
						defaultValues={createEntryFormDefaults(entry)}
						documentName={entry.document_name}
						narrativeSlot={narrativeSlot}
						narrativeActionSlot={narrativeActionSlot}
						unitsActionSlot={unitsActionSlot}
						hideGenerateNarrativeButton={hideGenerateNarrativeButton}
						onRegenerateNarrative={onRegenerateNarrative}
						banners={
							<EditEntryBanners
								entry={entry}
								failedExports={failedExports}
								onResolveFeedback={onResolveFeedback}
							/>
						}
						onSubmit={(values) => onSubmit(entry, values, selectedStatus)}
						onExport={(values) => onExport(entry, values)}
						validateOnMount
						readOnly={isDeleted}
						unitsReadOnly={unitsReadOnly}
						activeTimerReadOnlyNote={activeTimerReadOnlyNote}
					/>
				)}
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

interface EditEntryBannersProps {
	entry: Entry;
	failedExports: ReadonlyMap<number, string>;
	onResolveFeedback: (entryId: number) => void;
}

interface FeedbackResolveAction {
	label: string;
	onClick: () => void;
}

function EditEntryBanners({
	entry,
	failedExports,
	onResolveFeedback,
}: EditEntryBannersProps) {
	const feedbackResolveAction: FeedbackResolveAction = {
		label: "Resolve",
		onClick: () => onResolveFeedback(entry.id),
	};

	return (
		<>
			{failedExports.has(entry.id) && (
				<Banner variant="error" title="Export Failed">
					{failedExports.get(entry.id)}
				</Banner>
			)}
			{entry.attorney_feedback && (
				<Banner
					variant="warning"
					title="Admin Feedback"
					action={feedbackResolveAction}
				>
					{entry.attorney_feedback}
				</Banner>
			)}
		</>
	);
}
